import React from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { StaticMap } from '@vis.gl/react-google-maps';
import { BuildingColumnsIcon, MapMarkerIcon } from 'components/icons';
import { DashboardJobsite } from 'containers/dashboard/types';
import { useJobsiteStaticMapUrl } from 'utils/maps';

const commonClasses = {
  ellipsis: 'odin-whitespace-nowrap odin-overflow-hidden odin-text-ellipsis',
  statNumber: 'odin-text-gray-900 odin-text-xl odin-font-semibold',
  zeroStatNumber: 'odin-text-gray-400 odin-text-xl odin-font-semibold',
};

const classesByFormat = {
  list: {
    ...commonClasses,
    outerContainer: cn(
      'odin-flex odin-w-full odin-overflow-hidden odin-cursor-pointer',
      'odin-rounded-xl odin-border-gray-100 odin-border-2',
      'hover:odin-shadow-lg odin-duration-200 odin-ease-in',
      'odin-p-4 odin-items-center odin-justify-between',
    ),
    photoContainer: cn(
      'odin-hidden sm:odin-block odin-h-[60px] odin-w-[60px] odin-min-w-[60px] odin-rounded-xl odin-overflow-hidden',
    ),
    infoContainer: cn(
      'odin-flex odin-flex-col sm:odin-flex-row odin-grow odin-justify-between odin-overflow-hidden',
    ),
    detailContainer: 'odin-pl-4 odin-flex-1 odin-min-w-0 odin-overflow-hidden',
    statsContainer: cn(
      'odin-flex odin-gap-6 odin-justify-between odin-shrink-0 odin-pt-2.5 sm:odin-pt-0',
    ),
    statContainer: cn('odin-flex odin-flex-col odin-pl-4'),
  },
  tile: {
    ...commonClasses,
    outerContainer: cn(
      'odin-flex odin-flex-col odin-overflow-hidden odin-cursor-pointer',
      'odin-rounded-xl odin-w-full odin-min-h-[400px] odin-max-h-[400px] odin-max-w-[450px]',
      'odin-shadow-md hover:odin-shadow-xl odin-duration-200 odin-ease-in',
    ),
    photoContainer: cn('odin-h-[60%] odin-max-h-[60%] odin-overflow-hidden'),
    infoContainer: cn(
      'odin-h-[40%] odin-flex odin-flex-col odin-justify-between odin-px-6 odin-py-4',
    ),
    detailContainer: '',
    statsContainer: cn('odin-flex odin-justify-between'),
    statContainer: cn('odin-flex odin-flex-col'),
  },
};

type DashboardJobsiteTileProps = {
  jobsite: DashboardJobsite;
  asListRow: boolean;
};

export function DashboardJobsiteTile({
  jobsite,
  asListRow,
}: DashboardJobsiteTileProps): React.ReactElement {
  const staticMapUrl = useJobsiteStaticMapUrl(
    jobsite?.latitude,
    jobsite?.longitude,
  );

  const classes = asListRow ? classesByFormat.list : classesByFormat.tile;

  return (
    <Link
      className={classes.outerContainer}
      to={`/jobsite/${jobsite.jobsiteId}`}
      data-testid={`jobsite-tile-container-${jobsite.jobsiteId}`}
      style={{ color: 'inherit' }}
    >
      <div className={classes.photoContainer}>
        {jobsite.profilePicture?.downloadUrl ? (
          <img
            className="odin-w-full odin-h-full odin-object-cover"
            src={jobsite.profilePicture?.downloadUrl}
            alt={`${jobsite.name}`}
          />
        ) : (
          staticMapUrl && (
            <StaticMap
              className={asListRow && 'odin-max-h-[60px]'}
              url={staticMapUrl}
            />
          )
        )}
      </div>
      <div className={classes.infoContainer}>
        <div className={classes.detailContainer}>
          <h2
            className={cn(
              'odin-text-gray-900 odin-text-xl odin-font-semibold',
              classes.ellipsis,
            )}
            data-testid="jobsite-tile-name-display"
          >
            {jobsite.name}
          </h2>
          <p
            className={classes.ellipsis}
            data-testid="jobsite-tile-city-state-display"
          >
            <MapMarkerIcon className="odin-text-odin-primary odin-pr-2.5" />
            {jobsite.addressCity}, {jobsite.addressState}
            <BuildingColumnsIcon className="odin-text-odin-primary odin-px-2.5" />
            {jobsite.developer?.name}
          </p>
        </div>
        <div className={classes.statsContainer}>
          <div className={classes.statContainer}>
            <h2
              className={
                jobsite.jobsiteStats.numberOfActiveWorkers > 0
                  ? classes.statNumber
                  : classes.zeroStatNumber
              }
              data-testid="jobsite-tile-active-workers-ct"
            >
              {jobsite.jobsiteStats.numberOfActiveWorkers}
            </h2>
            <p>Workers</p>
          </div>
          <div className={classes.statContainer}>
            <h2
              className={
                jobsite.jobsiteStats.numberOfOnSiteWorkers > 0
                  ? classes.statNumber
                  : classes.zeroStatNumber
              }
              data-testid="jobsite-tile-on-site-workers-ct"
            >
              {jobsite.jobsiteStats.numberOfOnSiteWorkers}
            </h2>
            <p>On-site</p>
          </div>
          <div className={classes.statContainer}>
            <h2
              className={
                jobsite.jobsiteStats.numberOfOrientationWorkersInProgress > 0
                  ? classes.statNumber
                  : classes.zeroStatNumber
              }
              data-testid="jobsite-tile-orientation-workers-ct"
            >
              {jobsite.jobsiteStats.numberOfOrientationWorkersInProgress}
            </h2>
            <p>Onboarding</p>
          </div>
        </div>
      </div>
    </Link>
  );
}
